import { GapUnit } from '@/models/appointment/AppointmentChainConfig.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
// The gap picker offers hours in half-hour steps (see GAP_UNIT_STEP), so intraday snapping rounds to 30 minutes.
const MINUTES_PER_HALF_HOUR = MINUTES_PER_HOUR / 2;

export type GapBound = { unit: GapUnit; value: number };

export const GAP_UNIT_MAX: Record<GapUnit, number> = {
    [GapUnit.MINUTES]: 59,
    [GapUnit.HOURS]: 23,
    [GapUnit.DAYS]: 1000,
};

export const GAP_UNIT_STEP: Record<GapUnit, number> = {
    [GapUnit.MINUTES]: 1,
    [GapUnit.HOURS]: 0.5,
    [GapUnit.DAYS]: 1,
};

export const GAP_UNIT_LABEL: Record<GapUnit, string> = {
    [GapUnit.MINUTES]: 'Min',
    [GapUnit.HOURS]: 'Hrs',
    [GapUnit.DAYS]: 'Days',
};

const intradayToMinutes = (value: number, unit: GapUnit): number =>
    unit === GapUnit.HOURS ? Math.round(value * MINUTES_PER_HOUR) : value;

export const totalMinutesToGapBound = (totalMinutes: number): GapBound => {
    if (totalMinutes <= GAP_UNIT_MAX[GapUnit.MINUTES]) return { unit: GapUnit.MINUTES, value: totalMinutes };
    if (totalMinutes <= GAP_UNIT_MAX[GapUnit.HOURS] * MINUTES_PER_HOUR)
        return { unit: GapUnit.HOURS, value: Math.round(totalMinutes / MINUTES_PER_HALF_HOUR) / 2 };
    return {
        unit: GapUnit.DAYS,
        value: Math.min(Math.round(totalMinutes / MINUTES_PER_DAY), GAP_UNIT_MAX[GapUnit.DAYS]),
    };
};

export const gapInputToIsoDuration = (value: number, unit: GapUnit): string =>
    unit === GapUnit.DAYS ? `P${value}D` : `PT${intradayToMinutes(value, unit)}M`;

export const parseGapBound = (iso: string, round = true): GapBound => {
    const totalMinutes = Math.round(dayjs.duration(iso).asMinutes());
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return { unit: GapUnit.MINUTES, value: 0 };
    if (round) return totalMinutesToGapBound(totalMinutes);
    return iso.includes('T')
        ? { unit: GapUnit.MINUTES, value: totalMinutes }
        : { unit: GapUnit.DAYS, value: Math.round(totalMinutes / MINUTES_PER_DAY) };
};

export const isValidGapValue = (value: number | undefined, unit: GapUnit): boolean => {
    if (value === undefined || !Number.isFinite(value) || value < 0 || value > GAP_UNIT_MAX[unit]) return false;
    if (unit === GapUnit.HOURS) return Number.isInteger(value * 2);
    return Number.isInteger(value);
};

const gapBoundToTotalMinutes = (value: number, unit: GapUnit): number =>
    unit === GapUnit.DAYS ? value * MINUTES_PER_DAY : intradayToMinutes(value, unit);

export const compareGapBounds = (aValue: number, aUnit: GapUnit, bValue: number, bUnit: GapUnit): number =>
    gapBoundToTotalMinutes(aValue, aUnit) - gapBoundToTotalMinutes(bValue, bUnit);

export const addGapBounds = (aValue: number, aUnit: GapUnit, bValue: number, bUnit: GapUnit): GapBound =>
    totalMinutesToGapBound(gapBoundToTotalMinutes(aValue, aUnit) + gapBoundToTotalMinutes(bValue, bUnit));

export type GapLocale = 'en' | 'de';

type GapLocaleConfig = {
    units: Record<GapUnit, [string, string]>;
    unitSeparator: string;
};

const GAP_LOCALES: Record<GapLocale, GapLocaleConfig> = {
    en: {
        units: {
            [GapUnit.MINUTES]: ['m', 'm'],
            [GapUnit.HOURS]: ['h', 'h'],
            [GapUnit.DAYS]: ['d', 'd'],
        },
        unitSeparator: '',
    },
    de: {
        units: {
            [GapUnit.MINUTES]: ['Min.', 'Min.'],
            [GapUnit.HOURS]: ['Std.', 'Std.'],
            [GapUnit.DAYS]: ['Tag', 'Tage'],
        },
        unitSeparator: ' ',
    },
};

export const gapUnitWord = (unit: GapUnit, value: number, locale: GapLocale = 'en'): string => {
    const [singular, plural] = GAP_LOCALES[locale].units[unit];
    return value === 1 ? singular : plural;
};

export const formatUnitLabel = (value: number, unit: GapUnit, locale: GapLocale = 'en'): string =>
    `${value}${GAP_LOCALES[locale].unitSeparator}${gapUnitWord(unit, value, locale)}`;

export const formatGapBound = (iso: string, locale: GapLocale = 'en'): string => {
    const { unit, value } = parseGapBound(iso);
    return formatUnitLabel(value, unit, locale);
};

const GAP_UNITS_DESCENDING = [GapUnit.DAYS, GapUnit.HOURS, GapUnit.MINUTES] as const;

export const formatExactGapMinutes = (totalMinutes: number, locale: GapLocale = 'en'): string => {
    let remaining = totalMinutes;
    const parts: string[] = [];
    for (const unit of GAP_UNITS_DESCENDING) {
        const minutesPerUnit = gapBoundToTotalMinutes(1, unit);
        const value = Math.floor(remaining / minutesPerUnit);
        remaining -= value * minutesPerUnit;
        if (value > 0) parts.push(formatUnitLabel(value, unit, locale));
    }
    return parts.length > 0 ? parts.join(' ') : formatUnitLabel(0, GapUnit.MINUTES, locale);
};

export const formatGapRange = (minIso: string, maxIso?: string, locale: GapLocale = 'en'): string => {
    const min = parseGapBound(minIso);
    const minLabel = min.value === 0 ? formatUnitLabel(0, GapUnit.MINUTES, locale) : formatGapBound(minIso, locale);

    if (maxIso === undefined) return `${minLabel} – ∞`;

    const max = parseGapBound(maxIso);
    // A zero-to-zero gap is "back-to-back" — there is nothing meaningful to show, so callers skip rendering it.
    if (min.value === 0 && max.value === 0) return '';
    if (min.unit === max.unit && min.value === max.value) return formatGapBound(maxIso, locale);
    if (min.unit === max.unit) return `${min.value} – ${formatUnitLabel(max.value, max.unit, locale)}`;
    return `${minLabel} – ${formatGapBound(maxIso, locale)}`;
};
